import React, { useEffect, useState } from "react";

/**
 * PWA Splash Screen — mostrata all'apertura dell'app installata (o alla prima visita di sessione)
 * per ~1.8s con logo FIFE animato. Rispetta la safe-area, si auto-dismette con fade-out.
 * Usa sessionStorage per non ricomparire ad ogni navigazione della stessa sessione.
 *
 * Personalizzazione: se l'utente è loggato (token presente) e la Dashboard ha memorizzato
 * il nome della squadra in localStorage (`fife:my_team_name`), lo splash mostra un saluto
 * caloroso "Bentornato, <SQUADRA>" con il girone come piccola pill sotto.
 */
export default function PWASplash() {
  const [show, setShow] = useState(false);
  const [fade, setFade] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [girone, setGirone] = useState("");

  useEffect(() => {
    // Mostra solo se: primo load della sessione E (PWA standalone OR mobile)
    const seen = sessionStorage.getItem("fife:splash_seen");
    if (seen) return;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    const isMobile = window.innerWidth < 900;
    if (!isStandalone && !isMobile) {
      sessionStorage.setItem("fife:splash_seen", "1");
      return;
    }
    // Personalized greeting: only when user is authenticated (token present) AND we have a
    // cached team name from a previous Dashboard load.
    let cachedName = "";
    let cachedGirone = "";
    try {
      const hasToken = !!localStorage.getItem("gufi_token");
      if (hasToken) {
        cachedName = (localStorage.getItem("fife:my_team_name") || "").trim();
        cachedGirone = (localStorage.getItem("fife:my_girone") || "").trim();
      }
    } catch {}
    setTeamName(cachedName);
    setGirone(cachedGirone);
    setShow(true);
    // Extend a bit when personalized so the affective moment lands.
    const holdMs = cachedName ? 2000 : 1400;
    const fadeMs = 500;
    const t1 = setTimeout(() => setFade(true), holdMs);
    const t2 = setTimeout(() => {
      setShow(false);
      sessionStorage.setItem("fife:splash_seen", "1");
    }, holdMs + fadeMs);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (!show) return null;
  const personalized = !!teamName;
  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${fade ? "opacity-0" : "opacity-100"}`}
      style={{
        background: "linear-gradient(135deg, #3b56e0 0%, #8b5cf6 45%, #f97316 100%)",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      data-testid="pwa-splash"
    >
      <div className="text-center px-6">
        <div className="pwa-splash-logo mx-auto rounded-3xl bg-white shadow-2xl flex items-center justify-center overflow-hidden"
             style={{ width: 148, height: 148 }}>
          <img
            src="/logo-fife-512.png"
            alt="FIFE — Federazione Italiana Fantaleghe Elite"
            className="w-[128px] h-[128px] object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
        <div className="pwa-splash-title mt-6 display font-black text-white text-4xl tracking-tight">
          FIFE
        </div>
        {personalized ? (
          <>
            <div className="pwa-splash-hello mt-4 text-white/90 text-xs sm:text-sm uppercase tracking-[0.32em] font-bold">
              Bentornato mister
            </div>
            <div className="pwa-splash-team mt-1 display font-black text-white text-2xl sm:text-3xl leading-tight px-3 max-w-[340px] mx-auto"
                 title={teamName}
                 data-testid="splash-team-name">
              {teamName}
            </div>
            {girone && (
              <div className="pwa-splash-girone mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur border border-white/30 text-white text-[11px] font-bold tracking-[0.22em] uppercase">
                Girone {girone}
              </div>
            )}
          </>
        ) : (
          <div className="pwa-splash-sub mt-2 text-white/95 text-[11px] sm:text-xs uppercase tracking-[0.28em] font-bold px-4 max-w-[320px] mx-auto leading-relaxed">
            Federazione Italiana Fantaleghe Elite
          </div>
        )}
        <div className="mt-8 flex justify-center gap-1.5">
          <span className="pwa-splash-dot" style={{ animationDelay: "0ms" }} />
          <span className="pwa-splash-dot" style={{ animationDelay: "160ms" }} />
          <span className="pwa-splash-dot" style={{ animationDelay: "320ms" }} />
        </div>
      </div>
      <style>{`
        @keyframes pwaSplashLogoIn {
          0%   { transform: scale(0.6) rotate(-8deg); opacity: 0; }
          60%  { transform: scale(1.08) rotate(3deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes pwaSplashFloat {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-6px); }
        }
        @keyframes pwaSplashFadeUp {
          0%   { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pwaSplashDot {
          0%, 60%, 100% { opacity: 0.35; transform: scale(0.9); }
          30%           { opacity: 1;    transform: scale(1.2); }
        }
        .pwa-splash-logo {
          animation: pwaSplashLogoIn 0.7s cubic-bezier(0.34,1.56,0.64,1) both,
                     pwaSplashFloat 2.4s ease-in-out 0.7s infinite;
        }
        .pwa-splash-title {
          animation: pwaSplashFadeUp 0.5s ease-out 0.35s both;
        }
        .pwa-splash-sub {
          animation: pwaSplashFadeUp 0.5s ease-out 0.55s both;
        }
        .pwa-splash-hello {
          animation: pwaSplashFadeUp 0.5s ease-out 0.55s both;
        }
        .pwa-splash-team {
          animation: pwaSplashFadeUp 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.7s both;
        }
        .pwa-splash-girone {
          animation: pwaSplashFadeUp 0.5s ease-out 0.9s both;
        }
        .pwa-splash-dot {
          display: inline-block;
          width: 10px; height: 10px; border-radius: 999px;
          background: white;
          animation: pwaSplashDot 1.1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
